import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { todayOutfit, closetItems, upcomingEvents, capsules, wishlist } from '../data/mockData';

const Dashboard = () => {
  const outfitItems = closetItems.filter(item => todayOutfit.items.includes(item.id));

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-12"
    >
      {/* Header & Status */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-2">
          <p className="text-runway-500 font-mono text-[10px] tracking-[0.3em] uppercase">Status: Optimal</p>
          <h1 className="font-editorial text-4xl lg:text-6xl tracking-tight">Bonjour, Elias.</h1>
          <p className="text-graphite-400 font-mono text-[10px] tracking-widest uppercase">142 ASSETS INDEXED • 92% UTILIZATION RATE</p>
        </div>
        <div className="glass-panel p-5 flex items-center gap-5 border-l-4 border-l-runway-500 shadow-xl">
          <div className="w-12 h-12 bg-pearl-100 flex items-center justify-center">
            <SafeIcon name="CloudRain" className="w-6 h-6 text-graphite-800" />
          </div>
          <div>
            <p className="font-bold text-xl leading-none">16°C</p>
            <p className="text-[10px] text-graphite-500 font-mono uppercase mt-1">Slight Precipitation</p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Today's Look Canvas */}
        <div className="lg:col-span-8 space-y-8">
          <div className="flex items-center justify-between border-b border-pearl-200 pb-4">
            <h2 className="font-bold tracking-[0.2em] uppercase text-[10px] text-graphite-400 flex items-center gap-3">
              <span className="w-2 h-2 bg-runway-500 rounded-full animate-pulse" />
              Live Canvas: Daily Composition
            </h2>
            <div className="flex gap-4">
              <button className="text-[9px] font-mono text-graphite-500 hover:text-runway-500 transition-colors uppercase tracking-widest flex items-center gap-2">
                <SafeIcon name="RefreshCw" className="w-3 h-3" /> Regenerate
              </button>
              <button className="text-[9px] font-mono text-graphite-500 hover:text-runway-500 transition-colors uppercase tracking-widest flex items-center gap-2">
                <SafeIcon name="Share2" className="w-3 h-3" /> Share
              </button>
            </div>
          </div>
          
          <div className="bg-white border border-pearl-200 p-8 md:p-16 relative overflow-hidden group shadow-sm">
            <div className="absolute top-0 right-0 p-8 z-20">
              <span className="bg-graphite-900 text-white text-[9px] font-mono px-4 py-1.5 uppercase tracking-[0.2em]">
                {todayOutfit.mood} Mood
              </span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
              {outfitItems.map((item, idx) => (
                <motion.div 
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="aspect-[3/4] bg-pearl-50 relative group/item border border-pearl-100 hover:border-runway-500 transition-all cursor-pointer"
                >
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-full h-full object-cover p-3 mix-blend-multiply group-hover/item:scale-105 transition-transform duration-700" 
                  />
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm p-4 opacity-0 group-hover/item:opacity-100 transition-all flex flex-col justify-end">
                    <p className="text-[9px] font-mono text-runway-500 uppercase mb-1">{item.type}</p>
                    <p className="font-bold text-[11px] uppercase tracking-tighter leading-none mb-1">{item.brand}</p>
                    <p className="text-[10px] text-graphite-400 line-clamp-1">{item.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-pearl-100 flex gap-6 items-start">
              <div className="w-10 h-10 bg-pearl-50 flex items-center justify-center flex-shrink-0 animate-bounce">
                <SafeIcon name="Wind" className="w-5 h-5 text-runway-500" />
              </div>
              <div className="space-y-3">
                <p className="text-lg text-graphite-800 italic font-editorial leading-tight max-w-xl">
                  "{todayOutfit.notes}"
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-[9px] font-mono text-graphite-400 uppercase tracking-widest">AI Synthesis • v2.4</span>
                  <span className="w-1 h-1 bg-pearl-300 rounded-full" />
                  <span className="text-[9px] font-mono text-graphite-400 uppercase tracking-widest">Confidence: 98%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-pearl-200 p-6 space-y-4 shadow-sm">
              <p className="text-[9px] font-mono uppercase tracking-widest text-graphite-400">Style Velocity</p>
              <div className="flex items-end gap-1.5 h-16">
                {[40, 70, 45, 90, 65, 85, 100].map((h, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    className="flex-1 bg-pearl-100 hover:bg-runway-500 transition-colors"
                  />
                ))}
              </div>
            </div>
            <div className="bg-graphite-900 text-white p-6 flex flex-col justify-between shadow-xl">
              <p className="text-[9px] font-mono uppercase tracking-widest text-pearl-400">Avg Cost Per Wear</p>
              <div>
                <p className="text-4xl font-editorial tracking-tighter">$12.40</p>
                <p className="text-[9px] font-mono text-runway-500 mt-2 uppercase tracking-widest">Trending Down -14%</p>
              </div>
            </div>
            <div className="bg-white border border-pearl-200 p-6 flex flex-col justify-between shadow-sm">
              <p className="text-[9px] font-mono uppercase tracking-widest text-graphite-400">Closet Value</p>
              <div>
                <p className="text-2xl font-bold tracking-tighter">$14,200</p>
                <p className="text-[9px] font-mono text-graphite-400 mt-2 uppercase tracking-widest">Insured Archive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Intel */}
        <div className="lg:col-span-4 space-y-12">
          {/* Upcoming Agenda */}
          <section className="space-y-6">
            <div className="flex items-center justify-between border-b border-pearl-200 pb-3">
              <h2 className="font-bold tracking-[0.2em] uppercase text-[10px] text-graphite-400">Agenda</h2>
              <SafeIcon name="ArrowRight" className="w-3.5 h-3.5 text-graphite-400" />
            </div>
            <div className="space-y-4">
              {upcomingEvents.map(event => (
                <div key={event.id} className="group cursor-pointer">
                  <div className="flex items-center justify-between p-4 bg-white border border-pearl-200 hover:border-runway-500 hover:translate-x-1 transition-all duration-300">
                    <div>
                      <p className="text-[9px] text-runway-500 font-mono mb-1 uppercase font-bold tracking-widest">{event.date}</p>
                      <p className="font-bold text-sm tracking-tight">{event.name}</p>
                    </div>
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      event.outfitStatus === 'Planned' ? 'bg-green-500' :
                      event.outfitStatus === 'In Progress' ? 'bg-yellow-500' :
                      'bg-pearl-200'
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Active Capsules */}
          <section className="space-y-6">
             <h2 className="font-bold tracking-[0.2em] uppercase text-[10px] text-graphite-400 border-b border-pearl-200 pb-3">Active Capsules</h2>
             <div className="grid grid-cols-2 gap-5">
               {capsules.map(cap => (
                 <div key={cap.id} className="group cursor-pointer">
                   <div className="aspect-[4/5] bg-pearl-100 overflow-hidden mb-3 relative shadow-sm">
                     <img src={cap.image} alt={cap.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 grayscale hover:grayscale-0" />
                     <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                       <p className="text-white text-[9px] font-mono uppercase font-bold tracking-widest">{cap.itemsCount} Items</p>
                     </div>
                   </div>
                   <p className="text-[11px] font-bold uppercase tracking-tight truncate">{cap.name}</p>
                 </div>
               ))}
             </div>
          </section>

          {/* Editorial Wishlist */}
          <section className="bg-white border border-pearl-200 p-8 shadow-sm">
            <h2 className="font-editorial italic text-2xl mb-6">The Wishlist</h2>
            <div className="space-y-6">
              {wishlist.map(item => (
                <div key={item.id} className="flex gap-5 items-center group cursor-pointer">
                  <div className="w-14 h-20 bg-pearl-50 shadow-sm overflow-hidden flex-shrink-0">
                    <img src={item.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-runway-500 mb-1">{item.brand}</p>
                    <p className="text-[11px] text-graphite-500 truncate font-medium">{item.name}</p>
                    <p className="text-[11px] font-mono font-bold mt-1">{item.price}</p>
                  </div>
                  <SafeIcon name="Plus" className="w-4 h-4 text-pearl-300 group-hover:text-graphite-900 transition-colors" />
                </div>
              ))}
            </div>
            <button className="w-full mt-8 py-3 border border-pearl-200 text-[10px] font-mono uppercase tracking-[0.2em] hover:bg-graphite-900 hover:text-white transition-all duration-300">
              Procurement Portal
            </button>
          </section>
        </div>
      </div>
    </motion.div>
  );
};

export default Dashboard;